import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import type { AudiencesBroadcastStatus } from "@/features/BroadcastV2/types";
import type { QueryClient } from "@tanstack/react-query";

import { broadcastV2ListQueryOptions } from "@/api/broadcastV2Audiences";
import { siteQueryOptions } from "@/api/siteDetails";
import { MaintenanceWindow } from "@/components/MaintenanceWindow";
import { BroadcastV2ListPage } from "@/features/BroadcastV2/components/BroadcastV2ListPage";
import {
  isBroadcastV2UnderMaintenance,
  useIsBroadcastV2UnderMaintenance,
} from "@/features/BroadcastV2/flag";

const V2_FILTERS = ["All", "Done", "Draft", "Active", "Archived"] as const;
type V2Filter = (typeof V2_FILTERS)[number];

const SEARCH_SCHEMA = z.object({
  broadcastId: z.string().optional(),
  page: z
    .union([z.string(), z.number()])
    .transform((v) => (typeof v === "string" ? Number(v) : v))
    .pipe(z.number().int().positive())
    .catch(1),
  pageSize: z
    .union([z.string(), z.number()])
    .transform((v) => (typeof v === "string" ? Number(v) : v))
    .pipe(z.number().int().positive())
    .catch(25),
  filter: z.enum(V2_FILTERS).catch("All"),
});

const V2_FILTER_STATUS_MAP: Record<
  Exclude<V2Filter, "All">,
  AudiencesBroadcastStatus
> = {
  Done: "BROADCAST_STATUS_DONE",
  Draft: "BROADCAST_STATUS_DRAFT",
  Active: "BROADCAST_STATUS_ACTIVE",
  Archived: "BROADCAST_STATUS_ARCHIVED",
};

export const Route = createFileRoute(
  "/_auth/dragon-tiles/$siteId/broadcast/list",
)({
  validateSearch: zodValidator(SEARCH_SCHEMA),
  loaderDeps: ({ search }) => [search],
  component: RouteComponent,
  loader: async ({ params, context, deps: [search] }) => {
    const { siteId } = params;
    const { queryClient } = context as { queryClient: QueryClient };

    // Under maintenance, the page renders a maintenance window — skip the
    // BroadcastV2 data fetching so it doesn't depend on the V2 backend.
    if (isBroadcastV2UnderMaintenance()) {
      return { siteId };
    }

    const selectedStatus =
      search.filter === "All" ? undefined : V2_FILTER_STATUS_MAP[search.filter];
    await Promise.all([
      queryClient.ensureQueryData(siteQueryOptions(siteId)),
      queryClient.ensureQueryData(
        broadcastV2ListQueryOptions(siteId, {
          page: search.page,
          limit: search.pageSize,
          status: selectedStatus,
        }),
      ),
    ]);
    return { siteId };
  },
});

function RouteComponent() {
  const { siteId } = Route.useParams();
  const isUnderMaintenance = useIsBroadcastV2UnderMaintenance();

  if (isUnderMaintenance) {
    return <MaintenanceWindow />;
  }

  return <BroadcastV2ListPage siteId={siteId} />;
}
